import type { FormEvent } from 'react';
import React from 'react';

import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  FormField,
  IconCalendarOutline,
  Input,
  Label,
} from '@zillow/constellation';

import type { Section } from '../../../models';
import { NOTICES_UTILITY_TERMINATION } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../constants/stringConstants';
import { disablePastDates } from '../../../constants/functionConstants';
import YesAndNo from '../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function NoticesOfCodeViolationsAndUtilityTermination({ section, setSectionValues }: Props) {
  function onNoNotices(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesNotices(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onUtilityServiceChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.UTILITY_SERVICE]: value,
    });
  }

  function onTerminationDateChange(date: Date): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.DATE_OF_TERMINATION]: date,
    });
  }

  function onNoUtilityTerminationCommon(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA]: false,
    });
  }

  function onYesUtilityTerminationCommon(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA]: true,
    });
  }

  return (
    <FormField
      label={
        <Label size="lg">
          In the past 12 months, have you received any notices from municipalities or utility providers of utility
          termination?
        </Label>
      }
      control={
        <YesAndNoExpansion
          yesHandler={onYesNotices}
          noHandler={onNoNotices}
          yesActive={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            marginTop="xs"
            label={<Label>Utility service to be terminated</Label>}
            control={
              <Input
                value={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.UTILITY_SERVICE)}
                onChange={onUtilityServiceChange}
                marginBottom="xs"
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>Date of utility service termination</Label>}
            control={
              <AdornedInput
                input={
                  <DatePickerInput
                    placeholder={DATE_PICKER_PLACEHOLDER}
                    value={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.DATE_OF_TERMINATION)}
                    onChange={onTerminationDateChange}
                    datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disablePastDates} />}
                  />
                }
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline />
                  </Adornment>
                }
              />
            }
          />
          <FormField
            label={<Label>Will the termination of the utility service affect the property or common areas?</Label>}
            control={
              <YesAndNo
                yesHandler={onYesUtilityTerminationCommon}
                noHandler={onNoUtilityTerminationCommon}
                yesActive={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA)}
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

NoticesOfCodeViolationsAndUtilityTermination.description = 'Chicago specific screen';

export default NoticesOfCodeViolationsAndUtilityTermination;
